import { useEffect, useState } from 'react'

/**
 * 订阅一条 CSS media query，返回当前是否命中。
 *
 * 与 `useIsMobile`（`src/hooks/use-mobile.tsx`）的区别是断点由调用方给：
 * 那个 hook 钉死在 768px 的「移动端」语义上，而某些组件的桌面/窄屏分叉在别的宽度
 * （AI 来源面板是 640px：≥640 用 Popover，<640 用底部 Sheet）。
 *
 * SSR / 不支持 matchMedia 的环境返回 false（当作宽屏），不抛。
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    // query 变化时初值要重新同步一次，否则会残留上一条 query 的结果
    setMatches(mql.matches)
    // Safari < 14 只有 addListener；两条路径都挂上，卸载时对称摘掉
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener?.(onChange)
    return () => mql.removeListener?.(onChange)
  }, [query])

  return matches
}
